import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { SITE } from "../data/site";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children, align = "left", light = false }) {
  return (
    <div className={`flex items-center gap-3 ${align === "center" ? "justify-center" : ""}`} data-testid="eyebrow-label">
      <span className="gold-rule-left" aria-hidden="true" />
      <span className={`text-xs font-semibold uppercase tracking-eyebrow ${light ? "text-gold" : "text-sage"}`}>
        {children}
      </span>
      {align === "center" && <span className="gold-rule-left" aria-hidden="true" />}
    </div>
  );
}

export function MaskedLines({ lines, className = "" }) {
  return (
    <span className={`block ${className}`}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
          <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay: 0.2 + i * 0.14, ease: EASE }}
          >
            {typeof line === "string" ? line : <em className="font-medium italic">{line.t}</em>}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

const BUTTON_COLORS = {
  plum: "bg-plum text-ivory hover:bg-berry shadow-lg shadow-plum/25",
  olive: "bg-sage text-ivory hover:bg-[#57603A] shadow-lg shadow-sage/25",
  gold: "bg-gold text-plum hover:bg-ivory shadow-lg shadow-gold/30",
  outline: "border-2 border-ivory/85 text-ivory hover:bg-ivory hover:text-plum",
};

export function ButtonLink({ href, color = "plum", children, testid, className = "" }) {
  return (
    <motion.span whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 22 }}>
      <Link
        to={href}
        data-testid={testid}
        className={`inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-center text-[12px] font-bold uppercase leading-snug tracking-[0.12em] transition-colors duration-300 sm:text-sm ${BUTTON_COLORS[color]} ${className}`}
      >
        {children}
      </Link>
    </motion.span>
  );
}

export function CallButton({ label = "Call to Order", testid = "call-to-order-button", className = "" }) {
  return (
    <motion.a
      href={SITE.phoneHref}
      data-testid={testid}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      className={`inline-flex items-center gap-2.5 rounded-full bg-plum px-6 py-3.5 text-center text-[12px] font-bold uppercase leading-snug tracking-[0.12em] text-ivory shadow-lg shadow-plum/25 transition-colors duration-300 hover:bg-berry sm:px-7 sm:text-sm ${className}`}
    >
      <Phone className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
      {label}: {SITE.phone}
    </motion.a>
  );
}

export function GhostLink({ href, children, testid }) {
  return (
    <Link
      to={href}
      data-testid={testid}
      className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-plum transition-colors duration-300 hover:text-berry"
    >
      <span className="border-b border-gold/60 pb-0.5 transition-colors duration-300 group-hover:border-berry">
        {children}
      </span>
      <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
    </Link>
  );
}
